import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { storageGet, storageSet, storageRemove, storageGetSync, isNativePlatform } from '../lib/storage';
import { runNativeMigration } from '../lib/storageMigration';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCESS_KEY = 'enferno_access_token_v1';
const REFRESH_KEY = 'enferno_refresh_token_v1';
const USER_KEY = 'enferno_user_v1';

// Send cookies cross-origin on every request (required for HttpOnly cookie auth on web).
axios.defaults.withCredentials = true;

const isNetworkError = (err) => {
  if (!err || err.response) return false;
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') return true;
  return /network|fetch|failed to fetch|timeout/i.test(err.message || '');
};

// Token-based auth:
//   Web  — HttpOnly cookies (set by backend). Tokens are NOT stored in
//          localStorage. _inMemoryToken holds the current session's access
//          token so the Authorization header stays fresh without touching
//          storage. Cleared on page reload; cookie re-authenticates via /auth/me.
//   Native (Capacitor) — encrypted Preferences (Keychain / EncryptedSharedPreferences).
//          Unchanged from before. isNativePlatform() gates the two paths.

// In-memory access token for the current web session. Populated on login /
// refresh; null on page reload (cookie handles re-auth via /auth/me).
let _inMemoryToken = isNativePlatform() ? storageGetSync(ACCESS_KEY) : null;

const applyAuthHeader = (access) => {
  _inMemoryToken = access || null;
  if (access) axios.defaults.headers.common.Authorization = `Bearer ${access}`;
  else delete axios.defaults.headers.common.Authorization;
};

const writeTokens = async (access, refresh) => {
  if (!isNativePlatform()) return; // web: HttpOnly cookies persist — nothing to write
  if (access) await storageSet(ACCESS_KEY, access);
  else await storageRemove(ACCESS_KEY);
  if (refresh) await storageSet(REFRESH_KEY, refresh);
  else await storageRemove(REFRESH_KEY);
};
const clearTokens = async () => {
  // Always remove legacy localStorage token keys — migration cleanup for sessions
  // that predated the HttpOnly cookie switch (LB-SEC-1, Jun 2026). New code never
  // writes tokens to localStorage on web, but old entries must be swept on logout.
  try { localStorage.removeItem(ACCESS_KEY); } catch { /* ignore */ }
  try { localStorage.removeItem(REFRESH_KEY); } catch { /* ignore */ }
  if (!isNativePlatform()) return; // web: server clears the cookie via clear_auth_cookies
  await storageRemove(ACCESS_KEY);
  await storageRemove(REFRESH_KEY);
};

// Bootstrap on native: pick up the stored token synchronously so the first
// API call already has the Authorization header set before any component mounts.
if (isNativePlatform() && _inMemoryToken) applyAuthHeader(_inMemoryToken);

// Every axios call must carry the latest Bearer token. Per-request `headers`
// (e.g. ProfileContext's If-Match) can otherwise replace the default
// Authorization header in axios 1.x — which surfaced as immediate 401s on
// PUT /api/profile even seconds after a fresh login.
// On web we read _inMemoryToken (set by applyAuthHeader / tryRefresh).
// On native we read from Preferences synchronously via storageGetSync.
axios.interceptors.request.use((config) => {
  const token = (
    isNativePlatform() ? (storageGetSync(ACCESS_KEY) || '') : (_inMemoryToken || '')
  ).trim();
  if (!token) return config;
  config.headers = config.headers ?? {};
  if (typeof config.headers.set === 'function') {
    config.headers.set('Authorization', `Bearer ${token}`);
  } else {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------- Refresh-token auto-rotation ----------
// On any 401 from the API, try to refresh the access token using the stored
// refresh token. If successful, replay the original request. If the refresh
// itself fails, sign the user out cleanly.
let _refreshing = null; // singleton in-flight promise to dedupe concurrent 401s

const tryRefresh = async () => {
  if (_refreshing) return _refreshing;
  _refreshing = (async () => {
    try {
      if (!isNativePlatform()) {
        // Web: HttpOnly refresh_token cookie is sent automatically — no body needed.
        const { data } = await axios.post(`${API}/auth/refresh`);
        applyAuthHeader(data.access_token); // update in-memory token only
        return data.access_token;
      }
      // Native: read the stored refresh token and send in the request body.
      const refreshToken = (await storageGet(REFRESH_KEY)) || storageGetSync(REFRESH_KEY);
      if (!refreshToken) return null;
      const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
      await storageSet(ACCESS_KEY, data.access_token);
      if (data.refresh_token) await storageSet(REFRESH_KEY, data.refresh_token);
      applyAuthHeader(data.access_token);
      return data.access_token;
    } catch {
      await clearTokens();
      applyAuthHeader(null);
      return null;
    } finally {
      _refreshing = null;
    }
  })();
  return _refreshing;
};

axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    // 429 Too Many Requests — show a friendly toast with Retry-After hint
    // instead of leaving the raw axios error for components to handle.
    // Single toast per burst (sonner de-dupes by id) so a 4-call burst
    // doesn't spam the user with 4 banners.
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers?.['retry-after'];
      const seconds = retryAfter ? parseInt(retryAfter, 10) : null;
      const wait = seconds && seconds < 120
        ? `Try again in ${seconds} second${seconds === 1 ? '' : 's'}.`
        : 'Take a breath and try again in a minute.';
      toast.error('Too many requests', {
        id: 'rate-limit',  // de-dupe key
        description: wait,
        duration: 6000,
      });
    }
    // 403 with `verification_required: true` — backend's hard-gate. Redirect
    // to /verify-email so the user can resend a fresh link. Single toast per
    // burst (sonner de-dupes by id) so multiple failed requests don't pile
    // up banners.
    const detail = error.response?.data?.detail;
    if (error.response?.status === 403 && detail && typeof detail === 'object' && detail.verification_required) {
      toast.error('Confirm your email to continue', {
        id: 'verification-required',
        description: detail.message || 'Your trial has ended. Confirm your email to keep using Enferno.',
        duration: 7000,
      });
      // Don't redirect if the user is already on the verify-email page.
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/verify-email')) {
        try { window.location.assign('/verify-email'); } catch { /* ignore */ }
      }
    }
    // Only retry once, only on 401, and skip the refresh endpoint itself
    if (
      error.response?.status === 401 &&
      !original._retried &&
      !(original.url || '').includes('/auth/refresh') &&
      !(original.url || '').includes('/auth/login')
    ) {
      original._retried = true;
      const newToken = await tryRefresh();
      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return axios(original);
      }
      // tryRefresh also failed — session is revoked or fully expired.
      // Clear the cached user so the next page load doesn't flash stale data,
      // then hard-navigate to root so AuthContext re-bootstraps cleanly.
      // Skip for /auth/me itself (that path is handled by fetchMe's catch block).
      const failingUrl = original.url || '';
      if (!failingUrl.includes('/auth/me')) {
        try { localStorage.removeItem('enferno_user_v1'); } catch { /* ignore */ }
        applyAuthHeader(null);
        try { window.location.assign('/'); } catch { /* ignore */ }
      }
    }
    return Promise.reject(error);
  },
);

const AuthContext = createContext({
  user: undefined,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

// FastAPI 422 returns array of {msg, ...} — flatten to a string for safe rendering.
export const formatApiError = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    // Optimistically rehydrate from cached user so the app boots authenticated
    // even when offline. The /auth/me call below confirms or invalidates.
    const cachedUserRaw = storageGetSync(USER_KEY);
    if (cachedUserRaw) {
      try { setUser(JSON.parse(cachedUserRaw)); } catch { /* corrupt — ignore */ }
    }

    // Native: the token lives in Preferences and may not have been picked up
    // by the synchronous bootstrap — re-hydrate before calling the server.
    // If there is still no token after async read, the user is definitely
    // logged out (native has no cookie fallback).
    if (isNativePlatform()) {
      let access = storageGetSync(ACCESS_KEY);
      if (typeof access === 'string') access = access.trim() || null;
      if (!access) {
        access = await storageGet(ACCESS_KEY);
        if (access) applyAuthHeader(access);
      }
      if (!access) {
        setUser(null);
        setLoading(false);
        return null;
      }
    }
    // Web: no local token check — HttpOnly cookie handles authentication.
    // We always call /auth/me; if the cookie is absent or expired, the server
    // returns 401 and we land in the catch block below.

    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      await storageSet(USER_KEY, JSON.stringify(data));
      return data;
    } catch (err) {
      // Network down (offline) → keep the cached session intact.
      if (isNetworkError(err)) {
        return cachedUserRaw ? JSON.parse(cachedUserRaw) : null;
      }
      // Real auth failure (401/403) → wipe the cached session.
      await clearTokens();
      await storageRemove(USER_KEY);
      applyAuthHeader(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // On native, run the async Preferences-side migration before any read
    // of the token. The web sync migration already ran via storageMigration's
    // import-time side effect (see src/index.js).
    (async () => {
      await runNativeMigration();
      fetchMe();
    })();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    await clearTokens();
    await writeTokens(data.access_token, data.refresh_token);
    applyAuthHeader(data.access_token);
    await storageSet(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const register = async ({ email, password, name, inviteCode }) => {
    const { data } = await axios.post(`${API}/auth/register`, {
      email, password, name, invite_code: inviteCode,
    });
    await clearTokens();
    await writeTokens(data.access_token, data.refresh_token);
    applyAuthHeader(data.access_token);
    await storageSet(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const loginWithCode = async (email, code) => {
    const { data } = await axios.post(`${API}/auth/login-code/consume`, { email, code });
    await clearTokens();
    await writeTokens(data.access_token, data.refresh_token);
    applyAuthHeader(data.access_token);
    await storageSet(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    await clearTokens();
    await storageRemove(USER_KEY);
    // Drop the ProfileContext's "last synced user" snapshot so the next login
    // is treated as a cross-account boot (wipes local cache). Prevents A's
    // history from leaking into B's cloud when both use the same browser.
    try { localStorage.removeItem('enferno_last_synced_user_id_v1'); } catch { /* ignore */ }
    applyAuthHeader(null);
    setUser(null);
  };

  const refresh = fetchMe;

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithCode, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
